import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Role {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface RoleDialogProps {
  open: boolean;
  role: Role | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoleFormData {
  name: string;
  description: string;
  active: boolean;
}

const RoleDialog: React.FC<RoleDialogProps> = ({
  open,
  role,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    active: true
  });
  const [errors, setErrors] = useState<Partial<RoleFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!role;

  // Reset form when dialog opens/closes or role changes
  useEffect(() => {
    if (open) {
      if (role) {
        setFormData({
          name: role.name,
          description: role.description,
          active: role.active
        });
      } else {
        setFormData({
          name: '',
          description: '',
          active: true
        });
      }
      setErrors({});
    }
  }, [open, role]);

  // Handle input changes
  const handleInputChange = (field: keyof RoleFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<RoleFormData> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Role name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Role name cannot exceed 100 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Role description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Role description must be at least 10 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Role description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload: any = {
        name: formData.name.trim(),
        description: formData.description.trim()
      };

      // Add id for updates (active status is not included in update API)
      if (isEditing) {
        payload.id = role!.id;
      }

      const endpoint = isEditing ? 'api/roles/update' : 'api/roles/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} role`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            // Handle validation errors
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Role' : 'Add New Role'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the role information below'
              : 'Fill in the details to create a new role'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Role Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter role name (e.g., Admin, Manager, User)"
            variant="outlined"
          />

          <TextField
            label="Role Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            error={!!errors.description}
            helperText={errors.description}
            disabled={submitting}
            fullWidth
            multiline
            rows={4}
            placeholder="Describe the role's responsibilities and privileges"
            variant="outlined"
          />

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange('active')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.active
                      ? 'This role is currently active and available for assignment'
                      : 'This role is inactive and cannot be assigned to users'
                    }
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Role information for new roles */}
          {!isEditing && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                Role Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once created, this role will be available for assignment to users.
                You can modify the role's status later through the edit functionality.
              </Typography>
            </Box>
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} Role`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RoleDialog;